import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import { FragmentOf } from "gql.tada";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";
import { Button } from "@components/shared/core/Button";
import { RichText } from "@components/shared/core/RichText";
import { TextInput } from "@components/shared/core/TextInput";
import { sendApiRequest } from "@services/sendApiRequest";
import { useTranslation } from "@services/i18n";
import { Namespaces } from "@services/i18n/ObelixProvider";
import { OBELIX_FOOTER_KEYS } from "@utils/obelixKeys";
import type { FooterTextKey } from "./footerConfig";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as y from "yup";

/**
 * Local newsletter slice of the footer (Phase 4, R-404). The five microcopy
 * strings are `footerConfig` text keys resolved via `t()`; the rich entry
 * stays Contentful (A2) and flows straight to `<RichText>`.
 */
export interface NewsletterFooter {
  newsletterFormLabel: string;
  newsletterEmailFieldLabel: string;
  newsletterCta: string;
  submissionApproved: string;
  validEmailNote: string;
  newsletterSignUpInfoRichText: FragmentOf<typeof RichEntryFragment> | null;
}

const EmailSubmissionValidator = y.object({
  email: y.string().email().required(),
});

type NewsletterFormModel = y.InferType<typeof EmailSubmissionValidator>;

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p className="text-label text-balance">{children}</p>
    ),
  },
};

export const SimpleNewsletterSignupForm = ({ footer }: { footer: NewsletterFooter }) => {
  const { t } = useTranslation(Namespaces.footer);
  const tNewsletter = (key: string) => {
    const entry = OBELIX_FOOTER_KEYS[key as FooterTextKey];
    return t(entry.obelixKey, entry.label);
  };
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful, errors },
  } = useForm<NewsletterFormModel>({
    resolver: yupResolver(EmailSubmissionValidator),
  });

  const onSubmit = async ({ email }: NewsletterFormModel) => {
    try {
      await sendApiRequest('/signup', { email });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const isInvalid = isSubmitted && !isSubmitSuccessful && !!errors.email;

  return (
    <form className="pb-16 dsk:pb-0 dsk:max-w-60" onSubmit={handleSubmit(onSubmit)}>
      <p className="text-meta pb-3">{tNewsletter(footer.newsletterFormLabel)}</p>
      
      <TextInput
        className="w-full mb-1 !text-button-small"
        inputProps={{
          className: "!h-11",
          type: "email",
          autoComplete: "email",
          inputMode: "email",
          required: true,
          "aria-required": true,
          "aria-invalid": isInvalid || undefined,
          "aria-describedby": isInvalid ? "newsletter-email-error" : undefined,
          ...register("email"),
        }}
        label={tNewsletter(footer.newsletterEmailFieldLabel)}
        isInvalid={isInvalid}
      />

      {isInvalid && (
        <p id="newsletter-email-error" role="alert" className="text-brand-cream text-button-small mb-2">
          *{tNewsletter(footer.validEmailNote)}
        </p>
      )}

      <Button
        variant="light-on-blur"
        size="small"
        className="w-full"
        type="submit"
        loading={isSubmitting}
        disabled={isSubmitSuccessful}
      >
        {isSubmitSuccessful
          ? tNewsletter(footer.submissionApproved)
          : tNewsletter(footer.newsletterCta)}
      </Button>

      <div className="pt-3">
        <RichText
          content={footer.newsletterSignUpInfoRichText as FragmentOf<typeof RichEntryFragment> | null | undefined}
          options={options}
        />
      </div>
    </form>
  );
};
